import type { ComponentPropsWithoutRef } from 'react';
import React, { createRef, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Segment } from '@fxtr/i18n';
import type { AnyObject } from '$util/types';
import { DEFAULT_LAT_LNG } from '$util/gmaps';
import type { AppGarage } from '$page-router/api/getGaragesByLatLong';
import { Button, DesignType } from '@/components/atoms/Button';
import { getCloudinaryConfig } from '@/util/cloudinaryConfig';
import { context } from '@/contexts/GlobalContext';
import { Img } from '@/components/atoms/Img';

import styles from './index.module.scss';

let keepMap: google.maps.Map | null;
let previousSelectedGarageId = '';
const keepMarkers: AnyObject = {};
const mapRef = createRef<HTMLDivElement>();

interface LatLngProps {
  readonly lat?: number;
  readonly lng?: number;
}

export interface LocationProps {
  readonly name: string;
  readonly centerLatLng: LatLngProps;
  readonly zoom?: number;
  readonly zoomControl?: boolean;
  readonly zoomControlOptions?: string;
}

interface TranslationsProps {
  readonly expandMapText: string;
  readonly hideMapText: string;
}

export interface MapWithGarageMarkersProps extends ComponentPropsWithoutRef<'div'> {
  readonly location?: LocationProps;
  readonly currentPosition: google.maps.LatLng | google.maps.LatLngLiteral;
  readonly selectedGarageId: string;
  readonly setSelectedGarageId: (garageId: string) => void;
  readonly garages?: AppGarage[];
  readonly translations: TranslationsProps;
}

const getIcons = ({ segment, isFixterGarage }: AppGarage, cloudinaryUrl: string) => {
  if (segment === Segment.Motrio_FR) {
    return {
      iconActive:
        'https://res.cloudinary.com/fxtr/image/upload/v1739805243/motrio/icons/motrio_pin_active_vlltal.png',
      iconInactive:
        'https://res.cloudinary.com/fxtr/image/upload/v1739805245/motrio/icons/motrio_pin_inactive_cp7fqn.png',
    };
  }

  if (isFixterGarage) {
    return {
      iconActive: `${cloudinaryUrl}/icons/fixter_garage_map_pin_d7slup.svg`,
      iconInactive: `${cloudinaryUrl}/icons/unselected_fixter_pin_kgczbm.svg`,
    };
  }
  return {
    iconActive: `${cloudinaryUrl}/icons/non_fixter_garage_map_pin_yj1ctt.png`,
    iconInactive: `${cloudinaryUrl}/icons/non_fixter_garage_map_pin_disabled_m4ftro.png`,
  };
};

export function MapWithGarageMarkers({
  location = DEFAULT_LAT_LNG.UK,
  currentPosition,
  selectedGarageId,
  setSelectedGarageId,
  garages = [],
  translations: { expandMapText = 'Expand Map', hideMapText = 'Hide Map' },
  className,
}: MapWithGarageMarkersProps): JSX.Element {
  const CLOUDINARY_URL = getCloudinaryConfig();

  const mapInitialised = useRef(false);

  const [showMap, setShowMap] = useState(false);
  const [showLoader, setShowLoader] = useState(true);

  const setGarageMarker = useCallback(
    (garageData: AppGarage, imageUrl: string, markerWidth: number, markerHeight: number) => {
      if (!keepMap) return;

      const garageMarker = new window.google.maps.Marker({
        position: {
          lat: garageData.lat,
          lng: garageData.lng,
        },
        map: keepMap,
        icon: {
          url: imageUrl,
          scaledSize: new window.google.maps.Size(markerWidth, markerHeight),
        },
      });

      keepMarkers[garageData.garageId] = garageMarker;

      garageMarker.addListener('click', () => {
        setSelectedGarageId(garageData.garageId);
      });
    },
    [setSelectedGarageId]
  );

  const setSelectedGarageMarker = useCallback(
    (newSelectedGarageId: string) => {
      // Update the icon of the previous selected garage to inactive
      if (previousSelectedGarageId && keepMarkers[previousSelectedGarageId]) {
        const previousGarage = garages.find((g) => g.garageId === previousSelectedGarageId);

        if (previousGarage) {
          const previousIcon = getIcons(previousGarage, CLOUDINARY_URL)?.iconInactive;

          keepMarkers[previousSelectedGarageId].setIcon({
            url: previousIcon,
            scaledSize: new window.google.maps.Size(40, 50),
          });
        }
      }

      // Update the icon of the new selected garage to active
      if (keepMarkers[newSelectedGarageId]) {
        const selectedGarage = garages.find((g) => g.garageId === newSelectedGarageId);
        if (selectedGarage) {
          const activeIcon = getIcons(selectedGarage, CLOUDINARY_URL)?.iconActive;

          keepMarkers[newSelectedGarageId].setIcon({
            url: activeIcon,
            scaledSize: new window.google.maps.Size(60, 80),
          });
        }
      }

      // Update the previousSelectedGarageId reference
      previousSelectedGarageId = newSelectedGarageId;
    },
    [CLOUDINARY_URL, garages]
  );

  const setMarkers = useCallback(() => {
    garages.forEach((garage) => {
      if (!keepMarkers[garage.garageId]) {
        // Create a new marker if it doesn't exist
        const icon = getIcons(garage, CLOUDINARY_URL)?.iconInactive;
        if (icon) setGarageMarker(garage, icon, 40, 50);
      }
    });

    // Update the currently selected garage marker
    setSelectedGarageMarker(selectedGarageId);
  }, [garages, setSelectedGarageMarker, selectedGarageId, CLOUDINARY_URL, setGarageMarker]);

  const setCurrentLocation = useCallback(
    (userPosition: google.maps.LatLng | google.maps.LatLngLiteral) => {
      if (!keepMap || !userPosition.lat || !userPosition.lng) return;

      keepMap.setCenter(userPosition);

      // eslint-disable-next-line no-new
      new window.google.maps.Marker({
        position: userPosition,
        map: keepMap,
        icon: {
          url: `${CLOUDINARY_URL}/icons/Center_cetd1k.svg`,
          scaledSize: new window.google.maps.Size(30, 30),
        },
      });
    },
    [CLOUDINARY_URL]
  );

  const loadMap = useCallback(() => {
    if (!process.browser) return;
    setShowLoader(false);

    const { centerLatLng, zoom, zoomControl, zoomControlOptions } = location;
    const { lat: centerLat, lng: centerLng } = centerLatLng;

    if (!mapInitialised.current && mapRef.current instanceof HTMLDivElement && centerLat && centerLng) {
      const map = new window.google.maps.Map(mapRef.current, {
        center: {
          lat: centerLat,
          lng: centerLng,
        },
        zoom,
        mapTypeId: 'roadmap',
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        zoomControl,
        zoomControlOptions: {
          position:
            zoomControlOptions === 'TOP_RIGHT' ? window.google.maps.ControlPosition.TOP_RIGHT : undefined,
        },
      });
      map.data.setStyle(() => ({
        fillColor: '#58b0ff',
        fillOpacity: 0.4,
        strokeWeight: 1,
      }));

      keepMap = map;
      mapInitialised.current = true;
      setMarkers();
      if (currentPosition) setCurrentLocation(currentPosition);
    }
  }, [currentPosition, location, setCurrentLocation, setMarkers]);

  const initMap = useCallback(() => {
    if (window?.google_map_script_tag) {
      // when multiple components are initialised at the same time
      const waitScriptLoad = setInterval(() => {
        if (window.google) {
          loadMap();
          clearInterval(waitScriptLoad);
        }
      }, 100);
    } else if (!window?.google) {
      const apiKey = context.get('gMapsApiKey');
      if (!apiKey) return;
      window.google_map_script_tag = true;
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://maps.google.com/maps/api/js?key=${apiKey}`;
      script.async = true;
      const x = document.getElementsByTagName('script')[0];
      x?.parentNode?.insertBefore(script, x);
      script.addEventListener('load', () => {
        loadMap();
      });
    }
  }, [loadMap]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window?.innerWidth <= 768) return;
    initMap();
  }, [initMap]);

  useEffect(() => {
    setMarkers();
  }, [garages, setMarkers]);

  useEffect(() => {
    setSelectedGarageMarker(selectedGarageId);
  }, [selectedGarageId, setSelectedGarageMarker]);

  useEffect(() => {
    if (currentPosition) setCurrentLocation(currentPosition);
  }, [currentPosition, setCurrentLocation]);

  const showMapClicked = () => {
    if (!keepMap) initMap();
    setShowMap(!showMap);
  };

  return (
    <div className={classNames(MapWithGarageMarkers.displayName, styles.component, className)}>
      {showLoader && (
        <div className={styles.loader}>
          <Img
            src="https://res.cloudinary.com/fxtr/image/upload/v1656437008/landing/location/static-gmap_sbnbe3.png"
            alt="static map"
            key="left-img"
            quality={75}
            sizes="(max-width: 904px) 140px, 400px"
            fill
            provider="cloudinary"
          />
        </div>
      )}

      <div
        ref={mapRef}
        className={classNames(styles.map, showMap && styles.map__open, showLoader && styles.map__hidden)}
      />

      <Button
        className={classNames(styles.displayMapButton)}
        designType={DesignType.CTA_LIGHT}
        iconId={showMap ? 'arrows/arrow-up' : 'arrows/arrow-down'}
        onClick={() => showMapClicked()}
      >
        {showMap ? hideMapText : expandMapText}
      </Button>
    </div>
  );
}

MapWithGarageMarkers.displayName = 'MapWithGarageMarkers';
